import React, { useState, useEffect } from 'react';
import {
  Plus, FileText, AlertTriangle, BarChart3, TrendingUp,
  CheckCircle, Clock, RefreshCw, Layout,
} from 'lucide-react';
import DocumentsList from './DocumentsList';
import DocumentForm from './DocumentForm';
import DocumentTemplates from './DocumentTemplates';
import useEmployeeDocuments from '../../../hooks/useEmployeeDocuments';

const TABS = [
  { id: 'all',        label: 'Documentos',             icon: FileText      },
  { id: 'templates',  label: 'Plantillas',              icon: Layout        },
  { id: 'alerts',     label: 'Alertas de Vencimiento',  icon: AlertTriangle },
  { id: 'stats',      label: 'Estadísticas',            icon: BarChart3     },
  { id: 'compliance', label: 'Cumplimiento',             icon: TrendingUp    },
];

const fmt = (n) => parseInt(n || 0).toLocaleString('es-DO');

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  return new Date(s + 'T12:00:00').toLocaleDateString('es-DO');
};

export default function DocumentsModule() {
  const { loadStats, loadCompliance, loadPendingAlerts, loadExpired, loading } = useEmployeeDocuments();

  const [activeTab,          setActiveTab]          = useState('all');
  const [showForm,           setShowForm]           = useState(false);
  const [refreshKey,         setRefreshKey]         = useState(0);
  const [pendingAlerts,      setPendingAlerts]      = useState([]);
  const [expiredDocs,        setExpiredDocs]        = useState([]);
  const [stats,              setStats]              = useState(null);
  const [complianceData,     setComplianceData]     = useState(null);

  useEffect(() => {
    if (activeTab === 'alerts')     loadAlertsData();
    if (activeTab === 'stats')      loadStatsData();
    if (activeTab === 'compliance') loadComplianceData();
  }, [activeTab]); // eslint-disable-line

  const loadAlertsData = async () => {
    const [pending, expired] = await Promise.all([loadPendingAlerts(), loadExpired()]);
    if (pending.success)  setPendingAlerts(pending.documents  || []);
    if (expired.success)  setExpiredDocs(expired.documents    || []);
  };

  const loadStatsData = async () => {
    const result = await loadStats();
    if (result.success) setStats(result.stats);
  };

  const loadComplianceData = async () => {
    const result = await loadCompliance();
    if (result.success) setComplianceData(result);
  };

  const handleDocumentCreated = () => {
    setShowForm(false);
    setRefreshKey(k => k + 1);
  };

  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div />
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={15} />
          Nuevo Documento
        </button>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                    active
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">

          {/* Todos los documentos */}
          {activeTab === 'all' && <DocumentsList key={refreshKey} />}

          {/* Plantillas */}
          {activeTab === 'templates' && <DocumentTemplates />}

          {/* Alertas */}
          {activeTab === 'alerts' && (
            <AlertsTab
              loading={loading}
              pendingAlerts={pendingAlerts}
              expiredDocs={expiredDocs}
            />
          )}

          {/* Estadísticas */}
          {activeTab === 'stats' && (
            <StatsTab loading={loading} stats={stats} />
          )}

          {/* Cumplimiento */}
          {activeTab === 'compliance' && (
            <ComplianceTab loading={loading} complianceData={complianceData} />
          )}

        </div>
      </div>

      {/* Form */}
      {showForm && (
        <DocumentForm
          onClose={() => setShowForm(false)}
          onSuccess={handleDocumentCreated}
        />
      )}
    </div>
  );
}

// ── Sub-tabs ──────────────────────────────────────────────────

function AlertsTab({ loading, pendingAlerts, expiredDocs }) {
  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-8">
      <AlertSection
        title="Próximos a Vencer (30 días o menos)"
        icon={<AlertTriangle size={15} className="text-amber-500" />}
        items={pendingAlerts}
        emptyMsg="No hay documentos próximos a vencer"
        emptyColor="green"
        renderBadge={(doc) => (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            doc.dias_restantes <= 7  ? 'bg-red-100 text-red-700'    :
            doc.dias_restantes <= 15 ? 'bg-orange-100 text-orange-700' :
                                       'bg-amber-100 text-amber-700'
          }`}>
            {doc.dias_restantes}d
          </span>
        )}
        renderSub={(doc) => `Vence el ${fmtDate(doc.fecha_vencimiento)}`}
        subColor="text-amber-600"
      />

      <AlertSection
        title="Documentos Vencidos"
        icon={<AlertTriangle size={15} className="text-red-500" />}
        items={expiredDocs}
        emptyMsg="No hay documentos vencidos"
        emptyColor="green"
        renderBadge={() => (
          <span className="text-xs font-semibold px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
            Vencido
          </span>
        )}
        renderSub={(doc) => `Venció hace ${Math.abs(doc.dias_restantes)} días · ${fmtDate(doc.fecha_vencimiento)}`}
        subColor="text-red-600"
      />
    </div>
  );
}

function AlertSection({ title, icon, items, emptyMsg, emptyColor, renderBadge, renderSub, subColor }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        {icon}{title}
      </h3>

      {items.length === 0 ? (
        <div className={`flex items-center gap-2 px-4 py-3 bg-${emptyColor}-50 border border-${emptyColor}-200 rounded-lg text-sm text-${emptyColor}-700`}>
          <CheckCircle size={15} /> {emptyMsg}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(doc => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900">{doc.documento_nombre}</p>
                <p className="text-xs text-gray-500 mt-0.5">{doc.empleado_nombre}</p>
                <p className={`text-xs mt-0.5 ${subColor}`}>{renderSub(doc)}</p>
              </div>
              {renderBadge(doc)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatsTab({ loading, stats }) {
  if (loading) return <LoadingSpinner />;
  if (!stats) return <EmptyMsg text="No hay estadísticas disponibles" />;

  const g = stats.general || {};
  const CARDS = [
    { label: 'Total',     value: g.total_documentos, color: 'text-gray-900'  },
    { label: 'Aprobados', value: g.approved,          color: 'text-green-700' },
    { label: 'Pendientes',value: g.pending,            color: 'text-amber-700' },
    { label: 'Vencidos',  value: g.vencidos,           color: 'text-red-700'   },
  ];

  return (
    <div className="space-y-6">
      {/* Resumen */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {CARDS.map(c => (
          <div key={c.label} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{c.label}</p>
            <p className={`text-3xl font-bold mt-1 ${c.color}`}>{fmt(c.value)}</p>
          </div>
        ))}
      </div>

      {/* Por categoría */}
      {stats.by_category?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-100 flex items-center gap-2">
            <BarChart3 size={15} className="text-blue-500" />
            Por Categoría
          </h3>
          <div className="space-y-2">
            {stats.by_category.map(cat => (
              <div
                key={cat.codigo}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                    <FileText size={14} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{cat.nombre}</p>
                    <p className="text-xs text-gray-400">{cat.codigo}</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-gray-900">{fmt(cat.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ComplianceTab({ loading, complianceData }) {
  if (loading) return <LoadingSpinner />;
  if (!complianceData) return <EmptyMsg text="No hay datos de cumplimiento disponibles" />;

  const total       = complianceData.total_empleados       || 0;
  const incompletos = complianceData.empleados_incompletos  || 0;
  const completos   = total - incompletos;

  return (
    <div className="space-y-6">
      {/* Resumen */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Empleados', value: total,       color: 'text-gray-900'  },
          { label: 'Completos',       value: completos,   color: 'text-green-700' },
          { label: 'Incompletos',     value: incompletos, color: 'text-red-700'   },
        ].map(c => (
          <div key={c.label} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{c.label}</p>
            <p className={`text-3xl font-bold mt-1 ${c.color}`}>{fmt(c.value)}</p>
          </div>
        ))}
      </div>

      {/* Lista empleados con faltantes */}
      {complianceData.compliance?.length > 0 ? (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-100">
            Empleados con Documentos Faltantes
          </h3>
          <div className="space-y-2">
            {complianceData.compliance.map(emp => (
              <div key={emp.employee_id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{emp.employee_nombre}</p>
                    <p className="text-xs text-gray-400">{emp.employee_codigo}</p>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                    {emp.total_faltantes} faltantes
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {emp.documentos_faltantes.map((doc, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                      {doc.tipo_nombre}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <CheckCircle size={15} />
          Todos los empleados tienen sus documentos obligatorios completos
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16 text-gray-400">
      <RefreshCw size={20} className="animate-spin mr-2" />
      <span className="text-sm">Cargando...</span>
    </div>
  );
}

function EmptyMsg({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      <FileText size={32} className="text-gray-300 mb-2" />
      <p className="text-sm text-gray-400">{text}</p>
    </div>
  );
}
